import React, { cloneElement, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AiFillExclamationCircle } from 'react-icons/ai';
import { cx, useAnchorPosition, useDismiss } from './hooks';

/**
 * Small "Are you sure?" bubble attached to its child (usually a button).
 * onConfirm may return a promise; the OK button shows a spinner until it settles.
 */
const Popconfirm = ({
  title,
  description,
  onConfirm,
  onCancel,
  okText = 'OK',
  cancelText = 'Cancel',
  okDanger = false,
  disabled = false,
  placement = 'top',
  children,
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const anchorRef = useRef(null);
  const bubbleRef = useRef(null);
  const position = useAnchorPosition(anchorRef, open, { placement, align: 'end' });

  const close = () => {
    setOpen(false);
    setBusy(false);
  };

  useDismiss([anchorRef, bubbleRef], open, () => {
    if (!busy) close();
  });

  const confirm = async () => {
    try {
      const result = onConfirm?.();
      if (result && typeof result.then === 'function') {
        setBusy(true);
        await result;
      }
    } finally {
      close();
    }
  };

  const trigger = cloneElement(children, {
    onClick: e => {
      if (disabled) return;
      e.stopPropagation();
      setOpen(o => !o);
    },
    'aria-expanded': open,
  });

  return (
    <span ref={anchorRef} className="popconfirm">
      {trigger}
      {open &&
        position &&
        createPortal(
          <div ref={bubbleRef} className="popconfirm-bubble" style={position} role="alertdialog">
            <div className="popconfirm-message">
              <AiFillExclamationCircle />
              <div>
                <div className="popconfirm-title">{title}</div>
                {description && <div className="popconfirm-desc">{description}</div>}
              </div>
            </div>
            <div className="popconfirm-actions">
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  onCancel?.();
                  close();
                }}
              >
                {cancelText}
              </button>
              <button
                type="button"
                className={cx('btn btn-sm btn-primary', okDanger && 'btn-danger')}
                onClick={confirm}
                aria-busy={busy || undefined}
                autoFocus
              >
                {okText}
              </button>
            </div>
          </div>,
          document.body
        )}
    </span>
  );
};

export default Popconfirm;
